import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import Sidebar from "../../../components/Sidebar";
import Popup from "../../../components/Popup";
import { getHomePageInfos, getFinancialInfos, updateFinancialInfos, deleteFinancialInfos } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { getPermissionControllers, hasCustomerPermission, hasBrokerPermission } from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import "../../css/FinancialInformation.css";

export default function FinancialInformation() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("bank"); // "bank" or "crypto"
  const [isEditing, setIsEditing] = useState(false);
  
  // Bank Information fields
  const [username, setUsername] = useState("");
  const [iban, setIban] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankCode, setBankCode] = useState("");
  
  // Crypto Information fields
  const [trc20, setTrc20] = useState("");
  const [erc20, setErc20] = useState("");
  const [trx, setTrx] = useState("");
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    username: false,
    iban: false,
    bankName: false,
    bankCode: false,
    trc20: false,
    erc20: false,
    trx: false,
  });

  // Get permission controllers from user data
  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const {
    customerPerm,
    customerDevicePerm,
    brokerPerm,
    brokerDevicePerm,
  } = permissions;

  // Check permissions for editing (using same permission as trading info)
  const canEdit = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_UPDATE_LEVERAGE
  ) || hasBrokerPermission(
    brokerPerm,
    brokerDevicePerm,
    PermissionBrokerModelController.PER__CAN_UPDATE_LEVERAGE
  );

  // Auto-dismiss messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Reset editing state when switching tabs
  useEffect(() => {
    setIsEditing(false);
  }, [activeTab]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Try to get user data from sessionStorage first (to avoid redundant API calls)
        let userDataFromStorage = null;
        try {
          const storedUserData = sessionStorage.getItem('userData');
          const storedTimestamp = sessionStorage.getItem('userDataTimestamp');
          
          // Use stored data if it exists and is less than 5 minutes old
          if (storedUserData && storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp, 10);
            if (age < 5 * 60 * 1000) { // 5 minutes
              userDataFromStorage = JSON.parse(storedUserData);
              setUser(userDataFromStorage);
            }
          }
        } catch (storageError) {
        }
        
        // Only fetch from API if we don't have cached data
        if (!userDataFromStorage) {
          const userResponse = await getHomePageInfos();
          const userData = userResponse?.decryptedData || userResponse?.data;
          
          if (userData && userData.CODE === AppConstants.responseOk && userData.DATA) {
            setUser(userData.DATA);
            // Store in sessionStorage for future use
            try {
              sessionStorage.setItem('userData', JSON.stringify(userData.DATA));
              sessionStorage.setItem('userDataTimestamp', Date.now().toString());
            } catch (storageError) {
            }
          }
        }

        // Fetch financial information from API
        const financialResponse = await getFinancialInfos();
        const financialData = financialResponse?.decryptedData || financialResponse?.data;
        
        // Check if response exists and has a CODE
        if (!financialData || typeof financialData !== 'object') {
          setError(null); // Don't show error for invalid format
        } else if (financialData.CODE === AppConstants.invalidInputs || financialData.CODE === 708) {
          // CODE 708 (INVALID_INPUTS) means no financial information exists yet
          // This is normal for new users - don't show error, just leave fields empty
          // Matches Flutter behavior: screen doesn't close, fields remain empty
          setError(null);
          setSuccess(null);
          // Fields remain empty (default state) - this is expected behavior
        } else if (financialData.CODE === AppConstants.responseOk) {
          // Extract bank and crypto information
          if (financialData.DATA) {
            const data = financialData.DATA;
            
            // Bank Information fields - check for bank object or direct fields
            if (data.bank) {
              setUsername(data.bank.USER_NAME || data.bank.USERNAME || data.bank.userName || "");
              setIban(data.bank.IBAN || data.bank.iban || "");
              setBankName(data.bank.NAME || data.bank.name || "");
              setBankCode(data.bank.CODE || data.bank.code || "");
            } else {
              // Fallback to direct fields
              setUsername(data.USERNAME || data.username || data.USER_NAME || "");
              setIban(data.IBAN || data.iban || "");
              setBankName(data.BANK_NAME || data.bankName || data.BANKNAME || data.NAME || "");
              setBankCode(data.BANK_CODE || data.bankCode || data.CODE || data.SWIFT || data.swift || "");
            }
            
            // Crypto Information fields - check for binance object (matches Flutter: DATA.binance)
            // But API response uses "binance" key, while update uses "crypto" key
            if (data.binance) {
              setTrc20(data.binance.TRC20 || data.binance.trc20 || "");
              setErc20(data.binance.ERC20 || data.binance.erc20 || "");
              setTrx(data.binance.TRX || data.binance.trx || "");
            } else if (data.crypto) {
              // Fallback to crypto key
              setTrc20(data.crypto.TRC20 || data.crypto.trc20 || "");
              setErc20(data.crypto.ERC20 || data.crypto.erc20 || "");
              setTrx(data.crypto.TRX || data.crypto.trx || "");
            } else {
              // Fallback to direct fields
              setTrc20(data.TRC20 || data.trc20 || "");
              setErc20(data.ERC20 || data.erc20 || "");
              setTrx(data.TRX || data.trx || "");
            }
          }
        } else {
          // Handle other error codes
          if (financialData?.CODE === 401) {
            setError(t("auth_failed_login_again", { ns: "mobile" }));
          } else {
            // Only show error for codes other than 708
            setError(financialData?.MESSAGE || `Error: CODE ${financialData?.CODE || 'Unknown'}`);
          }
        }
        
      } catch (error) {
        // Check if error response contains CODE 708
        const errorCode = error.response?.data?.CODE || 
                         error.response?.decryptedData?.CODE ||
                         error.decryptedData?.CODE;
        
        // Only set error if it's not a 708 code
        if (errorCode !== 708 && errorCode !== AppConstants.invalidInputs) {
          setError(error.message || t("financial_info_load_failed", { ns: "mobile" }));
        } else {
          // CODE 708 means no data - don't show error (normal case)
          setError(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEdit = () => {
    // Only enable editing for the currently active tab
    setIsEditing(true);
  };

  const handleSave = async () => {
    // Front-end validation: mark empty required fields with red border (no toast)
    const newFieldErrors = {
      username: false,
      iban: false,
      bankName: false,
      bankCode: false,
      trc20: false,
      erc20: false,
      trx: false,
    };

    if (activeTab === "bank") {
      if (!username.trim() || !iban.trim() || !bankName.trim() || !bankCode.trim()) {
        newFieldErrors.username = !username.trim();
        newFieldErrors.iban = !iban.trim();
        newFieldErrors.bankName = !bankName.trim();
        newFieldErrors.bankCode = !bankCode.trim();
        setFieldErrors(newFieldErrors);
        return;
      }
    } else if (activeTab === "crypto") {
      if (!trc20.trim() || !erc20.trim() || !trx.trim()) {
        newFieldErrors.trc20 = !trc20.trim();
        newFieldErrors.erc20 = !erc20.trim();
        newFieldErrors.trx = !trx.trim();
        setFieldErrors(newFieldErrors);
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Build the request body based on active tab
      let financialData;
      
      if (activeTab === "bank") {
        // Bank information
        financialData = {
          bank: {
            USER_NAME: username.trim(),
            IBAN: iban.trim(),
            NAME: bankName.trim(),
            CODE: bankCode.trim()
          }
        };
      } else {
        // Crypto information - use "binance" key in request body (matches API spec)
        // API expects: { "binance": { "TRC20": "...", "ERC20": "...", "TRX": "..." } }
        financialData = {
          binance: {
            TRC20: trc20.trim() || "",
            ERC20: erc20.trim() || "",
            TRX: trx.trim() || ""
          }
        };
      }
      // Call API to save financial information
      const response = await updateFinancialInfos(financialData);
      const result = response?.decryptedData || response?.data;
      if (result && result.CODE === AppConstants.responseOk) {
        setSuccess(t("financial_info_saved_successfully", { ns: "mobile" }));
        setError(null);
        setIsEditing(false);
        // Auto-dismiss success message after 5 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      } else {
        const errorMessage = result?.MESSAGE || `Error: CODE ${result?.CODE || 'Unknown'}`;
        setError(errorMessage);
        setSuccess(null);
        // Auto-dismiss error message after 5 seconds
        setTimeout(() => {
          setError(null);
        }, 5000);
      }
    } catch (error) {
      setError(error.message || t("financial_info_save_failed", { ns: "mobile" }));
      setSuccess(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeletePopup(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeletePopup(false);
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Determine type: "bank" or "binance" (for crypto) - matches Flutter FinancialType
      const type = activeTab === "bank" ? "bank" : "binance";
      // Call API to delete financial information
      const response = await deleteFinancialInfos(type);
      const result = response?.decryptedData || response?.data;
      if (result && result.CODE === AppConstants.responseOk) {
        setSuccess(
          activeTab === "bank"
            ? t("bank_info_deleted_successfully", { ns: "mobile" })
            : t("crypto_info_deleted_successfully", { ns: "mobile" })
        );
        setError(null);
        
        // Clear the fields after successful delete
        if (activeTab === "bank") {
          setUsername("");
          setIban("");
          setBankName("");
          setBankCode("");
        } else {
          setTrc20("");
          setErc20("");
          setTrx("");
        }
        
        // Auto-dismiss success message after 5 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      } else {
        const errorMessage = result?.MESSAGE || `Error: CODE ${result?.CODE || 'Unknown'}`;
        setError(errorMessage);
        setSuccess(null);
        // Auto-dismiss error message after 5 seconds
        setTimeout(() => {
          setError(null);
        }, 5000);
      }
    } catch (error) {
      setError(error.message || t("financial_info_delete_failed", { ns: "mobile" }));
      setSuccess(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="financial-information-page">
      <Sidebar user={user} />
      <div className="financial-information-content">
        <h1 className="financial-information-title">{t("financial_information", { ns: "mobile" })}</h1>
        
        {error && (
          <div className="financial-error-message">
            <strong>{t("error", { ns: "mobile" })}:</strong> {error}
          </div>
        )}
        {success && (
          <div className="financial-success-message">
            <strong>{t("success", { ns: "mobile" })}:</strong> {success}
          </div>
        )}
        {loading && (
          <div className="financial-loading-message">
            {t("loading", { ns: "mobile" })}
          </div>
        )}
        
        <div className="financial-information-container">
          {/* Tabs */}
          <div className="financial-tabs">
            <button
              className={`financial-tab ${activeTab === "bank" ? "financial-tab--active" : ""}`}
              onClick={() => setActiveTab("bank")}
              type="button"
            >
              {t("bank_accounts", { ns: "mobile" })}
            </button>
            <button
              className={`financial-tab ${activeTab === "crypto" ? "financial-tab--active" : ""}`}
              onClick={() => setActiveTab("crypto")}
              type="button"
            >
              {t("crypto_accounts", { ns: "mobile" })}
            </button>
          </div>

          {/* Tab Content */}
          <div className="financial-tab-content">
            {activeTab === "bank" && (
              <div className="financial-form">
                <div className="financial-form-row">
                  <div className="financial-form-group">
                    <label className="financial-form-label">{t("username", { ns: "mobile" })}</label>
                    <div className="financial-input-wrapper">
                      <span className="financial-input-icon">
                        <i className="fa-solid fa-user"></i>
                      </span>
                      <CustomInput
                        type="text"
                        placeholder={t("username", { ns: "mobile" })}
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                          if (fieldErrors.username) {
                            setFieldErrors((prev) => ({ ...prev, username: false }));
                          }
                        }}
                        required
                        className={`financial-input ${fieldErrors.username ? "financial-input--error" : ""}`}
                        disabled={!isEditing || activeTab !== "bank" || !canEdit}
                      />
                    </div>
                  </div>
                  
                  <div className="financial-form-group">
                    <label className="financial-form-label">{t("bank_name", { ns: "mobile" })}</label>
                    <div className="financial-input-wrapper">
                      <span className="financial-input-icon">
                        <i className="fa-solid fa-building"></i>
                      </span>
                      <CustomInput
                        type="text"
                        placeholder={t("bank_name", { ns: "mobile" })}
                        value={bankName}
                        onChange={(e) => {
                          setBankName(e.target.value);
                          if (fieldErrors.bankName) {
                            setFieldErrors((prev) => ({ ...prev, bankName: false }));
                          }
                        }}
                        required
                        className={`financial-input ${fieldErrors.bankName ? "financial-input--error" : ""}`}
                        disabled={!isEditing || activeTab !== "bank" || !canEdit}
                      />
                    </div>
                  </div>
                </div>

                <div className="financial-form-row">
                  <div className="financial-form-group">
                    <label className="financial-form-label">{t("iban", { ns: "mobile" })}</label>
                    <div className="financial-input-wrapper">
                      <span className="financial-input-icon">
                        <i className="fa-solid fa-credit-card"></i>
                      </span>
                      <CustomInput
                        type="number"
                        placeholder={t("iban", { ns: "mobile" })}
                        value={iban}
                        onChange={(e) => {
                          setIban(e.target.value);
                          if (fieldErrors.iban) {
                            setFieldErrors((prev) => ({ ...prev, iban: false }));
                          }
                        }}
                        required
                        className={`financial-input ${fieldErrors.iban ? "financial-input--error" : ""}`}
                        disabled={!isEditing || activeTab !== "bank" || !canEdit}
                      />
                    </div>
                  </div>
                  
                  <div className="financial-form-group">
                    <label className="financial-form-label">{t("bank_code", { ns: "mobile" })}</label>
                    <div className="financial-input-wrapper">
                      <span className="financial-input-icon">
                        <i className="fa-solid fa-hashtag"></i>
                      </span>
                      <CustomInput
                        type="number"
                        placeholder={t("bank_code", { ns: "mobile" })}
                        value={bankCode}
                        onChange={(e) => {
                          setBankCode(e.target.value);
                          if (fieldErrors.bankCode) {
                            setFieldErrors((prev) => ({ ...prev, bankCode: false }));
                          }
                        }}
                        required
                        className={`financial-input ${fieldErrors.bankCode ? "financial-input--error" : ""}`}
                        disabled={!isEditing || activeTab !== "bank" || !canEdit}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "crypto" && (
              <div className="financial-form">
                <div className="financial-form-row">
                  <div className="financial-form-group">
                    <label className="financial-form-label">{t("trc20", { ns: "mobile" })}</label>
                    <div className="financial-input-wrapper">
                      <span className="financial-input-icon">
                        <i className="fa-solid fa-coins"></i>
                      </span>
                      <CustomInput
                        type="text"
                        placeholder={t("trc20", { ns: "mobile" })}
                        value={trc20}
                        onChange={(e) => {
                          setTrc20(e.target.value);
                          if (fieldErrors.trc20) {
                            setFieldErrors((prev) => ({ ...prev, trc20: false }));
                          }
                        }}
                        required
                        className={`financial-input ${fieldErrors.trc20 ? "financial-input--error" : ""}`}
                        disabled={!isEditing || activeTab !== "crypto" || !canEdit}
                      />
                    </div>
                  </div>
                  
                  <div className="financial-form-group">
                    <label className="financial-form-label">{t("erc20", { ns: "mobile" })}</label>
                    <div className="financial-input-wrapper">
                      <span className="financial-input-icon">
                        <i className="fa-solid fa-coins"></i>
                      </span>
                      <CustomInput
                        type="text"
                        placeholder={t("erc20", { ns: "mobile" })}
                        value={erc20}
                        onChange={(e) => {
                          setErc20(e.target.value);
                          if (fieldErrors.erc20) {
                            setFieldErrors((prev) => ({ ...prev, erc20: false }));
                          }
                        }}
                        required
                        className={`financial-input ${fieldErrors.erc20 ? "financial-input--error" : ""}`}
                        disabled={!isEditing || activeTab !== "crypto" || !canEdit}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="financial-form-row">
                  <div className="financial-form-group financial-form-group--full-width">
                    <label className="financial-form-label">{t("trx", { ns: "mobile" })}</label>
                    <div className="financial-input-wrapper">
                      <span className="financial-input-icon">
                        <i className="fa-solid fa-coins"></i>
                      </span>
                      <CustomInput
                        type="text"
                        placeholder={t("trx", { ns: "mobile" })}
                        value={trx}
                        onChange={(e) => {
                          setTrx(e.target.value);
                          if (fieldErrors.trx) {
                            setFieldErrors((prev) => ({ ...prev, trx: false }));
                          }
                        }}
                        required
                        className={`financial-input ${fieldErrors.trx ? "financial-input--error" : ""}`}
                        disabled={!isEditing || activeTab !== "crypto" || !canEdit}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="financial-actions">
            {!isEditing ? (
              <>
                <CustomButton 
                  className="financial-edit-button"
                  variant="primary"
                  onClick={handleEdit}
                  disabled={!canEdit}
                >
                  <i className="fa-solid fa-pencil"></i>
                  <span>{t("edit", { ns: "mobile" })}</span>
                </CustomButton>
                <CustomButton 
                  className="financial-delete-button"
                  variant="primary"
                  onClick={handleDeleteClick}
                  disabled={!canEdit || saving}
                >
                  <i className="fa-solid fa-trash"></i>
                  <span>{t("delete", { ns: "mobile" })}</span>
                </CustomButton>
              </>
            ) : (
              <CustomButton 
                className="financial-save-button"
                variant="success"
                onClick={handleSave}
                disabled={saving || !canEdit}
              >
                <i className="fa-solid fa-check"></i>
                <span>{saving ? t("saving", { ns: "mobile" }) : t("save", { ns: "mobile" })}</span>
              </CustomButton>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Popup */}
      <Popup
        isOpen={showDeletePopup}
        onClose={handleDeleteConfirm}
        onCancel={() => setShowDeletePopup(false)}
        title={t("delete_financial_information", { ns: "mobile" })}
        message={t("delete_financial_information_msg", { ns: "mobile" })}
        buttonText={t("delete", { ns: "mobile" })}
        cancelButtonText={t("cancel", { ns: "mobile" })}
        showCancel={true}
        icon="fa-trash"
      />
    </div>
  );
}
